import * as ng from 'angular';
import {ComponentRegistration, ISiteConfigService} from '../core';
import {ICustomerSettingsService, IToastMessageService} from '../services';
import {ICreditCardTicketResource} from "../data/credit-card-ticket";
import SiteConfig from "../core/site-config";
import {IReCaptchaValidationResource} from "../data";
import {CreditCardControlState} from "./credit-card-control-state";

let template = require('./credit-card.html');

class CreditCardComponent extends ComponentRegistration {

    constructor() {
        super('creditCard', {
            controller: CreditCardController,
            templateUrl: template,
            controllerAs: 'creditCard',
            bindings: {
                cardType: "<",
                maskedNumber: "<",
                expMonth: "<",
                expYear: "<",
                token: "<",
                editOnly: "=?",
                creditCardControlState: "=?",
                userIdentifier: "=?",
                resetAfterSuccess: "=?",
                onTokenReceived: "&",
                onTokenCancelled: "&"
            }
        });
    }
}

interface ICreditCardController {
    updateFullExpiration(): void;
    validate(): void;
    onChange(): void;
    updateCreditCardModel(data): void;
    clearHiddenValues(elem): void;
    onPaymentProcessClick(): void;
    convertToFourDigitYear(yearPart): string;
}

class CreditCardController implements ICreditCardController {
    private static $inject: Array<string> = ['$scope', 'SiteConfig', '$sce', 'CustomerSettings', 'CreditCardTicket', 'ReCaptchaValidation', '$q',
        '$log', 'toastMessageFactory', ];

    //private fields
    private tokenUrl: string;
    private expirationDisplay: string;
  
    //bindings

    private cardType: string;
    private maskedNumber: string;
    private expMonth: string;
    private expYear: string;
    private token: string;
    private editOnly: Boolean;
    private _creditCardControlState: CreditCardControlState;
    private _recaptchaResponse: string;

    private _userIdentifier: string;
    private _resetAfterSuccess: Boolean;
    private _onTokenReceived: Function;
    private _onTokenCancelled: Function;

    constructor(private $scope: ng.IScope,
        private SiteConfig: ISiteConfigService,
        private $sce: ng.ISCEService,
        private CustomerSettings: ICustomerSettingsService,
        private CreditCardTicket: ICreditCardTicketResource,
        private ReCaptchaValidation: IReCaptchaValidationResource,
        private $q: ng.IQService,
        private $log: ng.ILogService,
        private toastMessageFactory: IToastMessageService) {

        console.log("creditCard controller loaded.");

    }

    public $onInit(): void {
        this.cardType = this.cardType || (this.maskedNumber) ? this.resolveCardTypeFromNumber(this.maskedNumber) : '';
        this.maskedNumber = this.maskedNumber || '';
        this.expMonth = this.expMonth || '';
        this.expYear = this.expYear || '';

        this.creditCardControlState = this.creditCardControlState || CreditCardControlState.ReadMode;
        this._recaptchaResponse = null;

        this.editOnly = this.editOnly || false;

        this.tokenUrl = this.tokenUrl || '';
        this.resetAfterSuccess = this.resetAfterSuccess || false;

        this.userIdentifier = this.userIdentifier || '';

        let self = this;
        var revokeMonthLister = this.$scope.$watch("creditCard.expMonth", () => { self.updateFullExpiration() });
        var revokeYearListener = this.$scope.$watch("creditCard.expYear", () => { self.updateFullExpiration() });

        this.$scope.$on("$destroy", () => {
            revokeMonthLister();
            revokeYearListener();
        });

        console.log("creditCard controller activated.");
    }

    public get userIdentifier() : string {
        return this._userIdentifier;
    }

    public set userIdentifier(value: string) {

        if (!value)
            return;

        this._userIdentifier = value;
    }

    public get resetAfterSuccess() : Boolean {
        return this._resetAfterSuccess;
    }

    public set resetAfterSuccess(value: Boolean) {
        this._resetAfterSuccess = value;
    }

    public get onTokenReceived() : Function {
        return this._onTokenReceived;
    }

    public set onTokenReceived(value: Function) {
        this._onTokenReceived = value;
    }

    public get onTokenCancelled() : Function {
        return this._onTokenCancelled;
    }

    public set onTokenCancelled(value: Function) {
        this._onTokenCancelled = value;
    }

    private get creditCardControlState() : CreditCardControlState {
        return this._creditCardControlState;
    }

    private set creditCardControlState(value: CreditCardControlState) {
        if (value === this._creditCardControlState)
            return;

        this._creditCardControlState = value;

        if (this._creditCardControlState === CreditCardControlState.EditMode) {

            // Lets refresh the token Url with a new ticket number every time we are in edit mode.
            // Also, if this.CustomerSettings is undefined, then the constructor was not called
            // yet (we are coming here because of binding set on credit-card control property).
            if (this.CustomerSettings && this.CreditCardTicket)
                this.getTokenUrl();
        }
        else {
            this.tokenUrl = '';
        }
    }

    private resolveCardTypeFromNumber(ccMaskedNumber: string): string {
        //this is a basic credit card type determinator, any card outside of the
        //major ones that have begining digits in the 
        //major card ranges will be returned as the major card type
        //this code is to be removed in a later hot fix once parcs stops sending null card types.
        var startingChar = ccMaskedNumber.length > 0 ? ccMaskedNumber[0] : '';
        if (startingChar == '') return '';
        switch (startingChar) {
            case '5':
            case '2':
                return "MasterCard";
            case '4':
                return "Visa";
            case '6':
                return "Discover";
            case '3':
                return "American Express"
            default:
                return '';
        }
    }

    public updateFullExpiration(): void {
        this.expirationDisplay = this.expMonth + "/" + this.expYear;
    }

    public validate(): void {
    
    }

    public onChange(): void {
        this.validate();
    }

    private getTokenUrl(): void {
        this.tokenUrl = '';

        this.CreditCardTicket.getNewTokenTicket({ id: this.userIdentifier, reCaptchaToken: this._recaptchaResponse }).$promise
            .then((ticketResponse) => {

                return this.CustomerSettings.getCreditCardSettings().then((settingResponse) => {

                    let url = settingResponse.newTokenUrl;
                   
                    url += "?redirectUrl=" + settingResponse.redirectHtml;
                    url += "&redirectError=false";
                    url += "&cssUrl=" + settingResponse.redirectCss;
                    url += '&appId=' + settingResponse.creditCardServiceAppId;
                    url += "&creditCardOperationTicket=" + ticketResponse.ticket;
                    url += "&showCloseButton=true";
                    url += "&showCreditCardTypes=true";
                    url += "&closeButtonText=Cancel";

                    let trustedUri = this.$sce.trustAsResourceUrl(url);
                    this.tokenUrl = trustedUri;
                    return this.$q.resolve(trustedUri);
                });
            })
            .catch((e) => {
                if (!this.resetAfterSuccess)
                    this.creditCardControlState = CreditCardControlState.ReadMode;

                this.$log.log(e, 'CreditCardTicketService: Failed to get a credit card operation request authorization for current user.');
                this.toastMessageFactory.showServerError('The credit card operation request failed.\r\nPlease try again, if the issue persists contact an administrator.');
                return this.$q.resolve(e);
            });
    }

    public updateCreditCardModel(data): void {
        this.token = data.token;
        this.maskedNumber = data.masked;
        this.expMonth = data.expiration.substring(2, 4);
        this.expYear = this.convertToFourDigitYear(data.expiration.substring(0, 2));
        this.cardType = data.type;
    }

    public clearHiddenValues(elem): void {
        // clear hidden input values
        elem.dataset.cancelled = null;
        elem.dataset.token = null;
        elem.dataset.masked = null;
        elem.dataset.expiration = null;
        elem.dataset.type = null;
    }
    
    public onPaymentProcessClick(): void {

        // read hidden input values
        var processPaymentData = document.getElementById('process-payment-data');

        var isCancelled = processPaymentData.dataset.cancelled === "true";

        if (!isCancelled)
            this.updateCreditCardModel(processPaymentData.dataset);

        this.clearHiddenValues(processPaymentData);

        // reset the display to read-only state
        if (!this.resetAfterSuccess)
            this.creditCardControlState = CreditCardControlState.ReadMode;

        //fire external event handler if exists
        if (isCancelled) {
            if (this.onTokenCancelled)
                this.onTokenCancelled();
        }
        else if (this.onTokenReceived) {
            let creditCard = {
                creditCardType: this.cardType,
                expirationMonth: this.expMonth,
                expirationYear: this.expYear,
                maskedCreditCard: this.maskedNumber,
                token: this.token
            }
            this.onTokenReceived({args: creditCard });
        }
    }

    public convertToFourDigitYear(yearPart): string {
        return ((yearPart.length === 2) ? '20' : '')
            + yearPart;
    }

    private onReCaptchaValidationCancelled() {
        if (this.onTokenCancelled)
            this.onTokenCancelled();
    }

    private onRecaptchaValidationSucceeded(response: string) : void {
        this._recaptchaResponse = response;
        this.creditCardControlState = CreditCardControlState.EditMode;
    }
}

let ccComponent = new CreditCardComponent();
export default ccComponent;
export { ccComponent as CreditCardComponent, ICreditCardController, CreditCardController }


// WEBPACK FOOTER //
// ./node_modules/source-map-loader!./app/user-controls/credit-card.ts